import { dataService } from './dataService';

const SUBSCRIPTION_CACHE_KEY = 'offline_subscription_status';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Check if user can perform offline operations
 * @param {string} operation - The operation being performed (orders, expenses, billing)
 * @returns {Promise<{allowed: boolean, reason?: string}>}
 */
export async function canPerformOfflineOperation(operation) {
  try {
    // Get cached subscription status
    const cachedStatus = await getCachedSubscriptionStatus();
    
    if (!cachedStatus) {
      // For trial users, allow offline operations by default
      // This is a fallback when subscription status is not cached
      console.log('No subscription status cached, allowing offline operations for trial users');
      return {
        allowed: true,
        reason: 'Offline operations allowed (trial user fallback)'
      };
    }

    // Check if cache is still valid (within 24 hours)
    const now = Date.now();
    const cacheAge = now - cachedStatus.timestamp;
    
    if (cacheAge > CACHE_DURATION) {
      // For trial users, allow operations even with expired cache
      if (cachedStatus.plan === 'trial' || !cachedStatus.plan) {
        console.log('Subscription cache expired, but allowing offline operations for trial user');
        return {
          allowed: true,
          reason: 'Offline operations allowed (trial user with expired cache)'
        };
      }
      
      return {
        allowed: false,
        reason: 'Subscription cache expired. Please go online to refresh subscription status.'
      };
    }

    // Check if subscription is valid
    if (!cachedStatus.valid) {
      return {
        allowed: false,
        reason: 'Subscription is not active. Please renew your subscription.'
      };
    }

    // Check operation-specific permissions
    const allowedOperations = getOfflineAllowedOperations(cachedStatus.plan);
    
    if (!allowedOperations.includes(operation)) {
      return {
        allowed: false,
        reason: `Operation '${operation}' is not allowed offline with your current plan.`
      };
    }

    return {
      allowed: true,
      reason: 'Offline operation allowed'
    };

  } catch (error) {
    console.error('Error checking offline operation permission:', error);
    return {
      allowed: false,
      reason: 'Error checking offline permissions. Please try again.'
    };
  }
}

/**
 * Cache subscription status for offline use
 * @param {Object} subscriptionStatus - The subscription status from server
 */
export async function cacheSubscriptionStatus(subscriptionStatus) {
  try {
    const cacheData = {
      ...subscriptionStatus,
      timestamp: Date.now(),
      cachedAt: new Date().toISOString()
    };

    await dataService.setMetadata(SUBSCRIPTION_CACHE_KEY, cacheData);
    console.log('Subscription status cached for offline use');
  } catch (error) {
    console.error('Failed to cache subscription status:', error);
  }
}

/**
 * Get cached subscription status
 * @returns {Promise<Object|null>}
 */
export async function getCachedSubscriptionStatus() {
  try {
    return await dataService.getMetadata(SUBSCRIPTION_CACHE_KEY);
  } catch (error) {
    console.error('Failed to get cached subscription status:', error);
    return null;
  }
}

/**
 * Get operations allowed offline based on subscription plan
 * @param {string} plan - The subscription plan
 * @returns {Array<string>} - Array of allowed operations
 */
function getOfflineAllowedOperations(plan) {
  const baseOperations = ['orders', 'expenses', 'billing'];
  
  switch (plan) {
    case 'admin':
    case 'pro':
    case 'premium':
      return [...baseOperations, 'customers', 'products'];
    
    case 'trial':
    case 'free':
      return baseOperations;
    
    default:
      return baseOperations;
  }
}

/**
 * Check if subscription cache is valid
 * @returns {Promise<boolean>}
 */
export async function isSubscriptionCacheValid() {
  try {
    const cachedStatus = await getCachedSubscriptionStatus();
    
    if (!cachedStatus) {
      return false;
    }

    const now = Date.now();
    const cacheAge = now - cachedStatus.timestamp;
    
    return cacheAge <= CACHE_DURATION;
  } catch (error) {
    console.error('Error checking subscription cache validity:', error);
    return false;
  }
}

/**
 * Clear subscription cache (force online check)
 */
export async function clearSubscriptionCache() {
  try {
    await dataService.removeMetadata(SUBSCRIPTION_CACHE_KEY);
    console.log('Subscription cache cleared');
  } catch (error) {
    console.error('Failed to clear subscription cache:', error);
  }
}

/**
 * Get offline operation status for UI display
 * @returns {Promise<{isOfflineAllowed: boolean, message: string, cacheValid: boolean}>}
 */
export async function getOfflineStatus() {
  try {
    const cacheValid = await isSubscriptionCacheValid();
    const cachedStatus = await getCachedSubscriptionStatus();
    
    if (!cachedStatus) {
      return {
        isOfflineAllowed: true,
        message: 'Offline operations allowed (trial user)',
        cacheValid: false
      };
    }

    if (!cacheValid) {
      // For trial users, allow operations even with expired cache
      if (cachedStatus.plan === 'trial' || !cachedStatus.plan) {
        return {
          isOfflineAllowed: true,
          message: 'Offline operations allowed (trial user)',
          cacheValid: false
        };
      }
      
      return {
        isOfflineAllowed: false,
        message: 'Subscription cache expired. Go online to refresh.',
        cacheValid: false
      };
    }

    if (!cachedStatus.valid) {
      return {
        isOfflineAllowed: false,
        message: 'Subscription not active',
        cacheValid: true
      };
    }

    return {
      isOfflineAllowed: true,
      message: 'Offline operations allowed',
      cacheValid: true
    };

  } catch (error) {
    console.error('Error getting offline status:', error);
    return {
      isOfflineAllowed: false,
      message: 'Error checking offline status',
      cacheValid: false
    };
  }
}

